import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, ArrowLeft, Users, Search, Pencil, Trash2, UserCheck, Gift, Zap, Loader2, X, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLE_LABELS = {
  user: "Accountant",
  client: "Client",
  admin: "Super Admin",
};
const ROLE_TONES = {
  user:   "bg-forest/10 text-forest",
  client: "bg-sky-100 text-sky-700",
  admin:  "bg-violet-100 text-violet-800",
};
const PLAN_LABELS = {
  trial:     { label: "Trial",     tone: "bg-ochre/10 text-ochre" },
  free:      { label: "Free",      tone: "bg-slate-100 text-slate-700" },
  operator:  { label: "Operator",  tone: "bg-forest/10 text-forest" },
  studio:    { label: "Studio",    tone: "bg-plum-100 text-plum-800 bg-violet-100 text-violet-800" },
};

export default function SuperAdminSettings() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // { user, kind }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (role !== "all") params.role = role;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/super-admin/users", { params });
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const stats = useMemo(() => ({
    total: users.length,
    accountants: users.filter((u) => u.role === "user").length,
    clients: users.filter((u) => u.role === "client").length,
    admins: users.filter((u) => u.role === "admin").length,
    free: users.filter((u) => u.subscription_plan === "free").length,
    comped: users.filter((u) => u.comp_until).length,
  }), [users]);

  const impersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.business_name || u.email}?`)) return;
    try {
      const endpoint = u.role === "user"
        ? `/super-admin/companies/${u.id}/impersonate`
        : `/super-admin/clients/${u.id}/impersonate`;
      await api.post(endpoint);
      window.location.href = u.role === "user" ? "/app" : "/client";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impersonation failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      const endpoint = u.role === "user"
        ? `/super-admin/companies/${u.id}`
        : `/super-admin/clients/${u.id}`;
      await api.delete(endpoint);
      toast.success("User deleted.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const setFree = async (u) => {
    if (!window.confirm(`Move ${u.email} to the free plan? Their Stripe subscription will cancel at period end.`)) return;
    try {
      await api.post(`/super-admin/users/${u.id}/set-free`);
      toast.success("Moved to free plan.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 space-y-6" data-testid="super-admin-settings-page">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/super-admin" className="text-ink-lighter hover:text-forest inline-flex items-center gap-1 text-sm" data-testid="back-to-super-admin">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-800 flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <div className="overline">Super Admin</div>
            <h1 className="font-heading text-3xl font-medium tracking-tight">Platform settings</h1>
            <p className="text-ink-light mt-1 text-sm">Manage every user on the platform. Change roles, comp months, move to free, or delete.</p>
          </div>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          <StatTile label="Users" value={stats.total} />
          <StatTile label="Accountants" value={stats.accountants} tone="forest" />
          <StatTile label="Clients" value={stats.clients} tone="sky" />
          <StatTile label="Admins" value={stats.admins} tone="violet" />
          <StatTile label="Free" value={stats.free} tone="slate" />
          <StatTile label="Comped" value={stats.comped} tone="ochre" />
        </div>

        {/* Filters */}
        <div className="card-surface p-4 border border-black flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-border bg-paper p-0.5" data-testid="role-filter">
            {[
              { v: "all", label: "All" },
              { v: "user", label: "Accountants" },
              { v: "client", label: "Clients" },
              { v: "admin", label: "Admins" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setRole(opt.v)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${role === opt.v ? "bg-forest text-white" : "text-ink-light hover:text-ink"}`}
                data-testid={`role-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-ink-lighter absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search email, name, or firm…"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest"
              data-testid="users-search-input"
            />
          </div>
          <button
            onClick={load}
            className="btn-secondary text-sm"
            data-testid="users-search-button"
          >
            Search
          </button>
        </div>

        {/* Users table */}
        <div className="card-surface overflow-hidden border border-black">
          {loading ? (
            <div className="p-8 text-center text-ink-lighter text-sm inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-ink-lighter text-sm">No users match.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-ink-lighter uppercase tracking-wider bg-paper/50">
                <tr>
                  <th className="text-left px-4 py-2">User</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Plan</th>
                  <th className="text-left px-4 py-2">Comp until</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-paper border border-border flex items-center justify-center text-[10px] font-semibold text-ink-lighter">
                            {(u.business_name || u.email || "?").slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.business_name || u.name || u.email}</div>
                          <div className="text-xs text-ink-lighter truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold uppercase px-2 py-0.5 ${ROLE_TONES[u.role] || "bg-slate-100"}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const plan = PLAN_LABELS[u.subscription_plan] || PLAN_LABELS.trial;
                        return (
                          <span className={`inline-block rounded-full text-[10px] font-medium px-2 py-0.5 ${plan.tone}`}>
                            {plan.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-light">
                      {u.comp_until ? new Date(u.comp_until).toISOString().slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setModal({ user: u, kind: "role" })}
                          className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                          title="Change role"
                          data-testid={`user-role-${u.id}`}
                        >
                          <Star className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          onClick={() => setModal({ user: u, kind: "comp" })}
                          className="p-1.5 rounded-md text-ink-lighter hover:text-ochre hover:bg-ochre/5"
                          title="Comp N months"
                          data-testid={`user-comp-${u.id}`}
                        >
                          <Gift className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          onClick={() => setFree(u)}
                          className="p-1.5 rounded-md text-ink-lighter hover:text-slate-700 hover:bg-slate-100"
                          title="Move to free plan"
                          data-testid={`user-setfree-${u.id}`}
                        >
                          <Zap className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        {u.role !== "admin" && (
                          <button
                            onClick={() => impersonate(u)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                            title="Impersonate"
                            data-testid={`user-impersonate-${u.id}`}
                          >
                            <UserCheck className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                        )}
                        {u.role !== "admin" && (
                          <button
                            onClick={() => del(u)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-destructive hover:bg-destructive/5"
                            title="Delete"
                            data-testid={`user-delete-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal?.kind === "role" && (
        <RoleModal user={modal.user} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.kind === "comp" && (
        <CompModal user={modal.user} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function StatTile({ label, value, tone = "slate" }) {
  const tones = {
    slate:  "bg-slate-100 text-slate-800",
    forest: "bg-forest/10 text-forest",
    sky:    "bg-sky-100 text-sky-800",
    violet: "bg-violet-100 text-violet-800",
    ochre:  "bg-ochre/10 text-ochre",
  };
  return (
    <div className={`rounded-xl border border-black p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-medium">{label}</div>
      <div className="font-heading text-2xl num mt-0.5">{value}</div>
    </div>
  );
}

function RoleModal({ user, onClose, onSaved }) {
  const [role, setRole] = useState(user.role || "client");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/super-admin/users/${user.id}/role`, { role });
      toast.success(`Role changed to ${ROLE_LABELS[role]}.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-testid="role-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Change role</div>
            <div className="font-heading text-lg font-medium">{user.email}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-paper" data-testid="role-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-6">
          {["user", "client", "admin"].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRole(r)}
              className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                role === r ? "border-forest bg-forest/5 text-forest" : "border-border text-ink-light hover:border-ink-lighter"
              }`}
              data-testid={`role-option-${r}`}
            >
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink" data-testid="role-cancel">Cancel</button>
          <button type="submit" disabled={saving || role === user.role} className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="role-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

function CompModal({ user, onClose, onSaved }) {
  const [months, setMonths] = useState(3);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/super-admin/users/${user.id}/comp`, { months });
      toast.success(`Comped ${months} month${months === 1 ? "" : "s"} · until ${data.comp_until.slice(0, 10)}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Comp failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-testid="comp-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Comp months</div>
            <div className="font-heading text-lg font-medium">{user.email}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-paper" data-testid="comp-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <label className="block mb-6">
          <span className="block text-xs text-ink-light mb-1">Months to comp</span>
          <input
            type="number"
            min={1}
            max={36}
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10) || 1)}
            className="w-full px-3 py-2 rounded-md border border-border text-sm focus:outline-none focus:border-forest num"
            data-testid="comp-months-input"
          />
          <p className="text-xs text-ink-lighter mt-1">Extends `comp_until` on the user by 30 × months days.</p>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink" data-testid="comp-cancel">Cancel</button>
          <button type="submit" disabled={saving || months < 1} className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="comp-save">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Comp {months} month{months === 1 ? "" : "s"}
          </button>
        </div>
      </form>
    </div>
  );
}
